import React, { useEffect, useRef } from 'react';

type TickerCallback<T> = (diff: number, prev: T) => T | Promise<T>;

export function useTicker<T>(cb: TickerCallback<T>, interval = 1000, instant = false) {
	const cbRef = useRef(cb);

	useEffect(() => {
		cbRef.current = cb; // Update the ref if `cb` changes
	}, [cb]);

	useEffect(() => {
		if (!interval) return; // Skip if no interval is provided

		let id: ReturnType<typeof setTimeout> | null = null;
		let lastTick = Date.now();
		let prev: T | null = null;
		let canceled = false;

		const tick = async () => {
			if (canceled) return;

			const now = Date.now();
			const diff = now - lastTick;
			if (prev !== null) {
				prev = await cbRef.current(diff, prev); // Execute the callback
			}

			if (!canceled) {
				lastTick = now;
				id = setTimeout(tick, interval);
			}
		};

		if (instant) tick(); // Run immediately if `instant` is true
		else id = setTimeout(tick, interval);

		return () => {
			canceled = true; // Mark as canceled to prevent further ticks
			if (id) clearTimeout(id); // Clear the timeout
		};
	}, [interval, instant]);
}
